import {ChangeDetectionStrategy, Component, computed, inject, Inject, signal} from '@angular/core';
import {AdminService, AdminUser} from "../../services/admin.service";
import {Constants} from "../../util/constants";
import {MAT_DIALOG_DATA, MatDialogClose} from "@angular/material/dialog";
import {BusyService} from "../../services/busy.service";
import {finalize} from "rxjs/operators";
import {AlertService} from "../../services/alert.service";
import {MatButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {MatInput} from '@angular/material/input';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {form, FormField, pattern, required} from "@angular/forms/signals";
import {FieldErrorsComponent} from "../../controls/field-errors/field-errors.component";

interface UserModel {
    email: string;
    role: string;
    active: boolean;
}

@Component({
    templateUrl: './user-dialog.component.html',
    styleUrls: ['./user-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatInput, MatError, MatLabel, MatSelect, MatOption, MatCheckbox, MatTooltip, MatButton, MatDialogClose, FormField, FieldErrorsComponent]
})
export class UserDialogComponent {
    readonly ROLES = ['Read Only', 'Admin User', 'Super Admin'];

    user?: AdminUser = inject<AdminUser>(MAT_DIALOG_DATA);

    userModel = signal<UserModel>({
        email: this.user?.email || '',
        role: this.user?.role || '',
        active: this.user?.active || false
    });

    userForm = form(this.userModel, (schemaPath) => {
        required(schemaPath.email, {message: Constants.ERR_REQUIRED});
        pattern(schemaPath.email, Constants.EMAIL_REGEX, {message: Constants.ERR_INVALID_EMAIL});
        required(schemaPath.role, {message: Constants.ERR_REQUIRED});
    });


    constructor(private adminService: AdminService,
                private busyService: BusyService,
                private alertService: AlertService) {
    }

    getCheckboxTooltip = computed(() =>
        this.userForm.active().value()? "Deactivate user" : "Activate user"
    );

    hasChanged = computed(() => {
        if (!this.user)
            return true;
        return this.userForm.email().value().trim().toLowerCase() != this.user.email ||
               this.userForm.active().value() != this.user.active ||
               this.userForm.role().value() != this.user.role;
    });

    submit(event: SubmitEvent) {
        event.preventDefault();
        this.busyService.showBusy();
        if (this.user) {
            const email = this.userForm.email().value().trim().trim().toLowerCase();
            this.adminService.updateUser(this.user.id, email, this.userForm.active().value(), this.userForm.role().value()).pipe(
                finalize(() => {this.busyService.showNotBusy()})
            ).subscribe({
                next: () => {
                },
                error: (err) => {
                    this.alertService.error("Error updating user: " + err.message);
                    console.error("Error updating user:", err);
                }
            });
        } else {
            this.adminService.addUser(this.userForm.email().value().trim(), this.userForm.active().value(), this.userForm.role().value()).pipe(
                finalize(() => {this.busyService.showNotBusy()})
            ).subscribe({
                next: () => {
                },
                error: (err) => {
                    this.alertService.error("Error adding user: " + err.message);
                    console.error("Error adding user:", err);
                }
            });
        }
    }

}
